import { Text } from '@/components'
import { ChargeForecast, ChargeSchedule } from '@/types'
import {
  getIsCharging,
  getIsPluggedIn,
  getUpcomingDREvent,
  getIsHome,
} from '../utils'
import * as Sentry from '@sentry/browser'
import { selectScheduleForChargeForecast } from '../selectors'
import { useAppSelector } from '@/hooks'
import ChargeReasons from './ChargeReasons'
import ChargeScheduleTime from './ChargeScheduleTime'
import { AlertMessage } from '@/app/components'
import { isControlledViaLimit } from '@/utils/vehicles/car'
import { useState } from 'react'
import { logEvent } from '@/logging'
import DROptOutModal from './DROptOutModal'
import { DeviceType } from '@/app/components/DeviceSelectionGallery/DeviceSelectionGallery.types'

type Props = {
  deviceType: 'vehicle' | 'vehicle_charger'
  deviceId: number
  chargeForecast: ChargeForecast
  chargeSchedule: ChargeSchedule
}

export type Mode =
  | 'NoCharge'
  | 'SmartChargeScheduled'
  | 'DREventScheduled'
  | 'CurrentlySmartCharging'
  | 'CurrentlyCharging'

const TitleMap: { [key in Mode]: string | null } = {
  NoCharge: null,
  SmartChargeScheduled: 'Smart charge scheduled',
  DREventScheduled: 'Smart charge scheduled',
  CurrentlySmartCharging: 'Currently smart charging',
  CurrentlyCharging: 'Currently Charging',
}

function mapToDeviceType(
  deviceType: 'vehicle' | 'vehicle_charger',
): DeviceType {
  return deviceType === 'vehicle' ? 'vehicle' : 'vehicleCharger'
}

export default function SmartChargeSchedule(props: Props) {
  const {
    mode,
    chargeForecast,
    chargeSchedule,
    deviceType,
    deviceId,
    onDrOptOutClick,
    drOptOutModalOpen,
    setOptedOutModalOpen,
    onDrOptOutModalClose,
    upcomingDREvent,
    stoppedChargeWithLimit,
  } = useViewModel(props)

  if (mode === 'NoCharge') {
    return null
  }

  return (
    <div>
      <AlertMessage
        className="mb-2"
        show={stoppedChargeWithLimit}
        variant="info"
      >
        We've temporarily lowered your charge limit to stop your car from
        charging due to your charge settings. We'll reset it to your preferred
        limit later.
      </AlertMessage>
      <div className="flex flex-col gap-4 p-5 rounded-xl bg-themed-base-100">
        <div>
          <Text variant="subheader" className="inline mr-2">
            {TitleMap[mode]}
          </Text>
        </div>

        <div className="flex justify-between w-full">
          <ChargeScheduleTime mode={mode} chargeForecast={chargeForecast} />
        </div>
        {mode !== 'CurrentlyCharging' && (
          <ChargeReasons
            mode={mode}
            chargeForecast={chargeForecast}
            chargeSchedule={chargeSchedule}
            deviceType={deviceType}
            deviceId={deviceId}
            onProgramEnrollmentClick={onDrOptOutClick}
          />
        )}
        {upcomingDREvent && (
          <DROptOutModal
            open={drOptOutModalOpen}
            onClose={onDrOptOutModalClose}
            drEvent={upcomingDREvent}
            onOptedOut={() => setOptedOutModalOpen(true)}
            deviceType={mapToDeviceType(deviceType)}
            helpLinkUrl="https://optiwatt.zendesk.com/hc/en-us/articles/44087312433179-What-should-I-expect-for-my-EV-during-Demand-Response-events"
          />
        )}
      </div>
    </div>
  )
}

export function useViewModel(props: Props) {
  const [optedOutModalOpen, setOptedOutModalOpen] = useState(false)
  const vehicles = useAppSelector((state) => state.vehicles)
  const vehicle =
    props.deviceType === 'vehicle'
      ? vehicles.vehicles?.find((vehicle) => vehicle.id === props.deviceId)
      : undefined
  const pluggedIn = getIsPluggedIn(props.chargeForecast)
  const isCharging = getIsCharging(props.chargeForecast)
  const isHome = getIsHome(props.chargeForecast)
  const upcomingDREvent = getUpcomingDREvent(props.chargeForecast)
  const allowsOptiwattManagedCharging = useAppSelector((state) =>
    Boolean(
      selectScheduleForChargeForecast(state, props.chargeForecast)
        ?.allow_charging_control,
    ),
  )

  let hasReachedLimit = false
  // If we are a vehicle, we need to send the mode function some additional data
  if (vehicle !== undefined) {
    if (
      vehicle?.battery_level_percent !== undefined &&
      vehicle?.battery_level_percent !== undefined
    ) {
      hasReachedLimit =
        vehicle?.battery_level_percent >=
        props.chargeSchedule.battery_target_max
    }
  }

  // @todo `mode` should be a record with a mode id and the data
  // needed to render the mode so that the we don't have to validate
  // the mode in the view before using data that is always available
  // for a given mode.
  // For example, we check if `upcomingDREvent` is truthy before
  // rendering the DrOptOutModal, when in reality we should be able
  // to just check if were in the DREventScheduled mode.
  const mode = getCurrentMode({
    pluggedIn,
    isCharging,
    isHome,
    upcomingDREvent: Boolean(upcomingDREvent),
    allowsOptiwattManagedCharging,
    hasReachedLimit,
  })

  const [drOptOutModalOpen, setDrOptOutModalOpen] = useState(false)

  const onDrOptOutClick = () => {
    logEvent('chargeforecast_smartschedule_droptout_click')
    setDrOptOutModalOpen(true)
  }
  const onDrOptOutModalClose = () => setDrOptOutModalOpen(false)

  let stoppedChargeWithLimit = false
  if (
    mode === 'SmartChargeScheduled' &&
    vehicle !== undefined &&
    isControlledViaLimit(vehicle.car)
  ) {
    stoppedChargeWithLimit = true
  }

  return {
    mode,
    drOptOutModalOpen,
    onDrOptOutClick,
    onDrOptOutModalClose,
    upcomingDREvent,
    stoppedChargeWithLimit,
    optedOutModalOpen,
    setOptedOutModalOpen,
    ...props,
  }
}

type GetCurrentModeArgs = {
  pluggedIn: boolean
  isCharging: boolean
  isHome: boolean
  upcomingDREvent: boolean
  allowsOptiwattManagedCharging: boolean
  hasReachedLimit: boolean
}

function getCurrentMode({
  pluggedIn,
  isCharging,
  isHome,
  upcomingDREvent,
  allowsOptiwattManagedCharging,
  hasReachedLimit,
}: GetCurrentModeArgs): Mode {
  if (!pluggedIn && isCharging) {
    Sentry.captureException(
      new Error(
        'Unexpected state: isCharging is true but the vehicle is not plugged in',
      ),
    )
    return 'NoCharge'
  }

  if (!pluggedIn || !isHome) {
    // if we are not plugged in or at home, we should still show the
    // upcoming dr event to allow users to opt out ahead of time.
    // Otherwise we can't show a forecast so we should show NoCharge.
    return upcomingDREvent ? 'DREventScheduled' : 'NoCharge'
  }

  if (hasReachedLimit) {
    return 'NoCharge'
  }

  if (isCharging) {
    return allowsOptiwattManagedCharging && isHome
      ? 'CurrentlySmartCharging'
      : 'CurrentlyCharging'
  }

  return allowsOptiwattManagedCharging ? 'SmartChargeScheduled' : 'NoCharge'
}
